import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import Combobox from "react-widgets/Combobox";
import "react-widgets/styles.css";
import { FieldValidation } from "../useCreateWizard";

interface ValidatedComboboxProps<T> {
    // Core props
    id?: string;
    value: T | undefined;
    data: T[];
    onChange: (value: T | undefined) => void;
    placeholder?: string;
    disabled?: boolean;
    busy?: boolean;

    // Display configuration
    textField: string | ((item: unknown) => string);
    dataKey: string | ((item: unknown) => string);
    renderListItem?: (props: { item: T }) => React.ReactElement;

    // Search configuration
    filter?: (item: T, searchValue: string) => boolean;

    // Validation
    validation?: FieldValidation;
    className?: string;
}

export function ValidatedCombobox<T>({
    validation,
    className,
    value,
    data,
    onChange,
    textField,
    dataKey,
    renderListItem,
    filter,
    ...props
}: ValidatedComboboxProps<T>) {
    const { t } = useTranslation("common");

    const hasError = validation && !validation.isValid && validation.errorMessage;

    // Combine validation styling with existing className
    const validationClass = hasError ? "is-invalid" : "";
    const combinedClassName = [className, validationClass].filter(Boolean).join(" ");

    // Create default case-insensitive filter function
    const defaultFilter = useCallback(
        (item: T, searchValue: string) => {
            if (!searchValue) return true;

            // Get display text for the item
            let displayText: string;
            if (typeof textField === "function") {
                displayText = textField(item);
            } else if (typeof textField === "string") {
                displayText = (item as any)[textField] || "";
            } else {
                displayText = String(item);
            }

            // Case-insensitive search
            return displayText.toLowerCase().includes(searchValue.toLowerCase());
        },
        [textField],
    );

    // Use provided filter or default filter
    const filterFunction = filter || defaultFilter;

    const handleChange = useCallback(
        (newValue: T | string | null) => {
            // Handle the case where user types a custom value (string)
            if (typeof newValue === "string") {
                // User typed something that doesn't match any item
                onChange(undefined);
                return;
            }

            onChange(newValue || undefined);
        },
        [onChange],
    );

    return (
        <Combobox<T>
            {...props}
            className={combinedClassName}
            value={value}
            data={data}
            onChange={handleChange}
            textField={textField}
            dataKey={dataKey}
            renderListItem={renderListItem}
            filter={filterFunction}
            messages={{
                emptyFilter: t("combobox.noFilterResults"),
                emptyList: t("combobox.emptyList"),
                openCombobox: t("combobox.openCombobox"),
            }}
        />
    );
}
